/**
 * Inbox search/filter helpers.
 * Extracted from ComHubUtils for use in worker-frontend.
 * ComHubUtils.tsx remains untouched for company-frontend compatibility.
 */
import { InboxQueryParamsEnum } from '@th-types/inbox-reviews.type';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';

// ─── Interfaces ────────────────────────────────────────────────────────────

export interface InboxSearchParams {
  searchText: string;
  dateRange: { startDate: Date | undefined; endDate: Date | undefined };
}

export interface InboxUrlParams {
  searchState?: InboxSearchParams;
  showOnlyNew?: boolean;
  offset?: number;
  mode?: string;
}

export interface InboxQueryParamsOptions {
  page: number;
  itemsPerPage: number;
  searchText?: string;
  dateRange?: {
    startDate: Date | undefined;
    endDate: Date | undefined;
  };
  isNew?: boolean;
  isUnread?: boolean;
  mode?: string;
}

// ─── Functions ─────────────────────────────────────────────────────────────

export const handleInboxSearch = (
  search: InboxSearchParams,
  currentSearchParams: URLSearchParams,
  updateSearchParams: (params: URLSearchParams) => void,
  options: { itemsPerPage?: number } = {}
) => {
  const { itemsPerPage = DEFAULT_ITEMS_PER_PAGE } = options;

  const newSearchParams = new URLSearchParams();

  if (search.searchText) {
    newSearchParams.set(InboxQueryParamsEnum.SEARCH_TEXT, search.searchText);
  }
  if (search.dateRange.startDate) {
    newSearchParams.set(
      InboxQueryParamsEnum.DATE_FROM,
      search.dateRange.startDate.toISOString()
    );
  }
  if (search.dateRange.endDate) {
    newSearchParams.set(
      InboxQueryParamsEnum.DATE_TO,
      search.dateRange.endDate.toISOString()
    );
  }

  // Preserve mode and unread from current URL params
  const mode = currentSearchParams.get(InboxQueryParamsEnum.MODE);
  if (mode) {
    newSearchParams.set(InboxQueryParamsEnum.MODE, mode);
  }
  const unread = currentSearchParams.get(InboxQueryParamsEnum.UNREAD);
  if (unread) {
    newSearchParams.set(InboxQueryParamsEnum.UNREAD, unread);
  }

  newSearchParams.set(InboxQueryParamsEnum.OFFSET, '0');
  newSearchParams.set(InboxQueryParamsEnum.MAX, itemsPerPage.toString());
  updateSearchParams(newSearchParams);
};

export const handleInboxClear = (
  updateSearchParams: (params: URLSearchParams) => void,
  options: { itemsPerPage?: number } = {}
) => {
  const { itemsPerPage = DEFAULT_ITEMS_PER_PAGE } = options;

  const newSearchParams = new URLSearchParams();
  newSearchParams.set(InboxQueryParamsEnum.OFFSET, '0');
  newSearchParams.set(InboxQueryParamsEnum.MAX, itemsPerPage.toString());
  updateSearchParams(newSearchParams);
};

// ─── URL-driven state helpers ─────────────────────────────────────────────

export function handleInboxPageChange(
  offset: number,
  searchParams: URLSearchParams,
  updateSearchParams: (params: URLSearchParams) => void
) {
  const newParams = new URLSearchParams(searchParams);
  newParams.set(InboxQueryParamsEnum.OFFSET, offset.toString());
  updateSearchParams(newParams);
}

export function handleInboxToggleUnread(
  currentValue: boolean,
  searchParams: URLSearchParams,
  updateSearchParams: (params: URLSearchParams) => void
) {
  const newParams = new URLSearchParams(searchParams);
  if (!currentValue) {
    newParams.set(InboxQueryParamsEnum.UNREAD, 'true');
  } else {
    newParams.delete(InboxQueryParamsEnum.UNREAD);
  }
  newParams.set(InboxQueryParamsEnum.OFFSET, '0');
  updateSearchParams(newParams);
}

export function handleInboxChatModeChange(
  mode: string | null,
  searchParams: URLSearchParams,
  updateSearchParams: (params: URLSearchParams) => void
) {
  const newParams = new URLSearchParams(searchParams);
  if (mode) {
    newParams.set(InboxQueryParamsEnum.MODE, mode);
  } else {
    newParams.delete(InboxQueryParamsEnum.MODE);
  }
  newParams.set(InboxQueryParamsEnum.OFFSET, '0');
  updateSearchParams(newParams);
}

export const extractInboxUrlParams = (
  searchParams: URLSearchParams
): InboxUrlParams => {
  const searchText = searchParams.get(InboxQueryParamsEnum.SEARCH_TEXT);
  const dateFrom = searchParams.get(InboxQueryParamsEnum.DATE_FROM);
  const dateTo = searchParams.get(InboxQueryParamsEnum.DATE_TO);
  const showNew = searchParams.get(InboxQueryParamsEnum.NEW);
  const showUnread = searchParams.get(InboxQueryParamsEnum.UNREAD);
  const offset = searchParams.get(InboxQueryParamsEnum.OFFSET);

  const result: InboxUrlParams = {};

  if (searchText || dateFrom || dateTo) {
    result.searchState = {
      searchText: searchText || '',
      dateRange: {
        startDate: dateFrom ? new Date(dateFrom) : undefined,
        endDate: dateTo ? new Date(dateTo) : undefined,
      },
    };
  }

  if (showNew !== null) {
    result.showOnlyNew = showNew === 'true';
  } else if (showUnread !== null) {
    result.showOnlyNew = showUnread === 'true';
  }

  if (offset !== null) {
    result.offset = Number(offset) || 0;
  }

  const mode = searchParams.get(InboxQueryParamsEnum.MODE);
  if (mode) {
    result.mode = mode;
  }

  return result;
};

export function buildInboxQueryParams({
  page,
  itemsPerPage,
  searchText,
  dateRange,
  isNew,
  isUnread,
  mode,
}: InboxQueryParamsOptions): URLSearchParams {
  const queryParams = new URLSearchParams();
  queryParams.append(InboxQueryParamsEnum.OFFSET, page.toString());
  queryParams.append(InboxQueryParamsEnum.MAX, itemsPerPage.toString());
  if (searchText) {
    queryParams.append(InboxQueryParamsEnum.SEARCH_TEXT, searchText);
  }
  if (dateRange?.startDate && dateRange?.endDate) {
    queryParams.append(
      InboxQueryParamsEnum.DATE_FROM,
      dateRange.startDate.toISOString()
    );
    queryParams.append(
      InboxQueryParamsEnum.DATE_TO,
      dateRange.endDate.toISOString()
    );
  }
  if (isNew) {
    queryParams.append(InboxQueryParamsEnum.NEW, 'true');
  }
  if (isUnread) {
    queryParams.append(InboxQueryParamsEnum.UNREAD, 'true');
  }
  if (mode) {
    queryParams.append(InboxQueryParamsEnum.MODE, mode);
  }
  return queryParams;
}
